import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { initializeAmplify } from './auth/intitalizeCognito'
import { Auth, Hub } from "aws-amplify";
import TracerServiceClient from "./clients/tracerServiceClient";
import { apiHost, groundingGenerationApiHost } from './config/apiConfig';
import { getDiscoverVideosRequestWithGroundingBody, getDiscoverImagesV2RequestWithGroundingBody, getDiscoverImagesV2RequestBody, getDiscoverVideosRequestBody } from "./helpers/exampleDiscoverImagesV2RequestBody";
import LLMAnswerGenerationClient from './helpers/llmAnswerGeneration';
import { Search, Mic, Camera, Settings, Menu, User, X, ChevronRight, ChevronDown } from 'lucide-react';

const currentUrl = window.location.origin;
const YoutubeEmbed = ({ embedId }) => {
  return (
    <iframe
      title="YouTube Video"
      width="100%"
      height="100%"
      src={`https://www.youtube.com/embed/${embedId}`}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
};

export default function App(props) {
   const [user, setUser] = useState(null);
   const [stage, setStage] = useState(getStage() || 'DEV');
   const [region, setRegion] = useState(getRegion() || 'us-west-2');
   const [serviceClient, setServiceClient] = useState(null);
   const [llmAnswerGenerationClient, setLLMAnswerGenerationClient] = useState(null);

  function getStage() {
    if (currentUrl.includes("beta")) {
      return "BETA";
    }
    if (currentUrl.includes("gamma")) {
      return "GAMMA";
    }
    if (currentUrl.includes("prod")) {
      return "PROD";
    }
    return "DEV";
  }

  function getRegion() {
    if (stage === "DEV" || stage === "BETA") {
      return "us-west-2";
    }
    if (currentUrl.includes("dub")) {
      return "eu-west-1";
    }
    
    if (currentUrl.includes("iad")) {
      return "us-east-1";
    }

    if (currentUrl.includes("pdx")) {
      return "us-west-2";
    }

    console.debug("Couldn't determine region. Defaulting to PDX");
    return "us-west-2"
  }
  
  function createAmplifyListener() {
    Hub.listen('auth', ({payload: {event, data}}) => {
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          console.debug("In cognito hosted ui: ", data)
          setUser({user: data});
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.debug('Sign in failure', data);
          break;
        default:
          console.debug("Event didn't match any of the expected events: ", event);
      }
    });
  }

  async function  doCognitoAuth(){
    //fetch the user credentials if midway has already authenticated the user.
    try {
      const user = await Auth.currentAuthenticatedUser();
      setUser({user: user});
      console.debug('Authenticated user ', user.getUsername());
    } catch(error) {
      console.debug('Not signed in. Error: ',  error);
      //if couldn't fetch credentials, sign in the user by federate:
      await Auth.federatedSignIn({customProvider: "FederateOIDC"});
    }
  }

  useEffect(() => {
    setServiceClient(new TracerServiceClient(apiHost[stage][region]));
  }, [stage, region]);

  useEffect(() => {
    setLLMAnswerGenerationClient(new LLMAnswerGenerationClient(groundingGenerationApiHost[stage][region]))
  }, [stage, region]);

  useEffect(() => {
    initializeAmplify(stage, region);
    createAmplifyListener();
    const doAuth = async () => {
      await doCognitoAuth();
    }
    doAuth();
  }, [stage, region]);

  const SideNavbar = () => {
    return (
      <div className='flex flex-col h-full'>
        <div className='p-4 shadow-sm'><Link to="/explorer"><p className='text-left text-blue-500'> Developer Experience</p></Link></div>
      </div>
    );
  };

  function TabItem({ label, active = false, icon = null, onClick }) {
    return (
      <div 
        className={`cursor-pointer px-2 py-1 border-b-2 transition-colors hover:text-white
          ${active ? 'border-blue-500 text-blue-400' : 'border-transparent hover:border-gray-600'}`}
        onClick={onClick}
      >
        {icon ? icon : label}
      </div>
    );
  }

  const MultimodalSearch = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [galleryView, setGalleryView] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [showSideNavbar, setShowSideNavbar] = useState(false);
    const [showImageGallery, setShowImageGallery] = useState(true);
    const [showVideoGallery, setShowVideoGallery] = useState(false);
    const [llmAnswer, setLLMAnswer] = useState("");
    const [finalQuery, setFinalQuery] = useState("");
    const [dataFetchFailed, setDataFetchFailed] = useState(false);
    const onHamburgerIconClick = () => setShowSideNavbar(!showSideNavbar);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [activeTab, setActiveTab] = useState('All');
    const searchInputRef = useRef(null);
    const [useGrounding, setUseGrounding] = useState(false);

    const toggleGrounding = () => {
      setUseGrounding(!useGrounding);
    };
    
    const invokeMMIRService = (query_text) => {
      async function fetchData() {
        if (useGrounding) {
          let llmAnswerGenerationClientResponse = await llmAnswerGenerationClient.getLLMAnswer(JSON.stringify({"query": query_text}, null, 2));
          setLLMAnswer(JSON.parse(llmAnswerGenerationClientResponse.body).llm_answer);
        }
        let images_request_body = getDiscoverImagesV2RequestWithGroundingBody(query_text, llmAnswer);
        let videos_request_body = getDiscoverVideosRequestWithGroundingBody(query_text, llmAnswer);
        if (!useGrounding || llmAnswer === '') {
          images_request_body = getDiscoverImagesV2RequestBody(query_text);
          videos_request_body = getDiscoverVideosRequestBody(query_text);
        }
        try {
          let images_response = await serviceClient.discoverImagesV2(images_request_body);
          let videos_response = await serviceClient.discoverImagesV2(videos_request_body);
          setDataFetchFailed(false);
          return {'images': images_response.images, 'videos': videos_response.videos.slice(0,5)};
        } catch (error) {
          setDataFetchFailed(true);
          return {'images': [], 'videos': []}; 
        }
      }
      return fetchData();
    }

    const handleSearch = e => {
      e.preventDefault();
      if (!searchQuery.trim()) {
        setIsSearching(false);
        return;
      }
      let queryText = searchQuery.trim();
      setFinalQuery(queryText);
      setShowSideNavbar(false);
      setIsSearching(true);
      setGalleryView(false); // Reset to grid view when searching
      setSelectedItem(null); // Clear selected image
      
      invokeMMIRService(queryText).then(response => {
        setImages(response.images);
        setVideos(response.videos);
        setIsSearching(false);
        setIsSearchFocused(false);
      });
    };

    // Handle search when pressing Enter in the input field
    const handleKeyPress = e => {
      if (e.key === 'Enter') {
        handleSearch(e);
      }
    };

    const openImageGallery = index => {
      setCurrentItemIndex(index);
      showImageGallery ? setSelectedItem(images[index]) : setSelectedItem(videos[index]);
      setGalleryView(true);
    };

    const closeGallery = () => {
      setGalleryView(false);
      setSelectedItem(null);
    };

    const chooseGalleryType = (gallery_type) => {
      if (gallery_type === "VIDEOS") {
        setShowImageGallery(false);
        setShowVideoGallery(true);
      } else {
        setShowImageGallery(true);
        setShowVideoGallery(false);
      }
    }

    const navigateGallery = direction => {
      const newIndex =
        (currentItemIndex + direction + images.length) % images.length;
      setCurrentItemIndex(newIndex);
      showImageGallery ? setSelectedItem(images[newIndex]) : setSelectedItem(videos[newIndex]);
    };

    // Handle keyboard navigation
    const handleKeyDown = e => {
      if (!galleryView) return;

      if (e.key === 'Escape') {
        closeGallery();
      } else if (e.key === 'ArrowRight') {
        navigateGallery(1);
      } else if (e.key === 'ArrowLeft') {
        navigateGallery(-1);
      }
    };

    const openVideoInNewTab = (url) => {
      window.open(url, "_blank", "noreferrer");
    };

    // Add keyboard event listener
    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [galleryView, currentItemIndex]);

    return (
      <div className='min-h-screen bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 font-sans'>
        {/* Header */}

        <header className="sticky top-0 z-10 bg-gray-800 p-3 flex items-center shadow-md">
        <div className="flex w-full items-center">
          <div className="mr-6 cursor-pointer">
            <h1 className='w-1/10 text-2xl font-bold text-blue-500 mr-8'>
              MMIR
            </h1>
          </div>
          
          <div className="relative flex-grow max-w-4xl mx-4" ref={searchInputRef}>
            <div className={`flex items-center bg-gray-700 rounded-full px-4 py-2 ${isSearchFocused ? 'ring-2 ring-blue-500' : ''}`}>
              <input 
                type="text" 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={() => setIsSearchFocused(true)}
                placeholder='search for images and videos'
                className="bg-transparent border-none outline-none text-white w-full" 
                ref={searchInputRef}
              />
              <button onClick={handleSearch}className="mx-1 text-gray-400 hover:text-gray-200 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors">
              <Settings size={20} />
            </button>
            <button className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors" onClick={onHamburgerIconClick}>
              {/* <Menu size={20} /> */}
            </button>
          </div>
        </div>
        </header>
        <nav className="bg-gray-800 text-gray-300 px-4 pb-2 border-b border-gray-700 sticky top-16 z-10">
          <div className="flex space-x-5 text-sm overflow-x-auto no-scrollbar">
            {['Images', 'Videos'].map((tab) => (
              <TabItem 
                key={tab} 
                label={tab} 
                active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              />
            ))}
            
            <div className="flex-grow"></div>
            <button 
              onClick={toggleGrounding}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${useGrounding ? 'bg-blue-600' : 'bg-gray-300'}`}
              aria-pressed={useGrounding}
              aria-label="Toggle grounding"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-300 ${useGrounding ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
        </nav>
        
        {/* Main content */}
        <main className='flex'>
          { showSideNavbar ? <SideNavbar /> : null }
          <div className='max-w-6xl mx-auto p-4 mt-4 flex flex-col'>
            {/* Image filters */}
            {images.length > 0 && (
              <div className='mb-6'>
                <div className='border-b border-gray-200 dark:border-gray-20 pb-2'>
                  <div className='flex space-x-6'>
                    <button className={showImageGallery ? 'flex-auto pb-2 border-b-2 border-blue-500 dark:border-blue-50 text-blue-500 dark:text-blue-50' : 'flex-auto pb-2 text-gray-600 dark:text-white-600 hover:text-gray-800 dark:hover:text-gray-80'} onClick={ () => chooseGalleryType("IMAGES") }>
                      Images
                    </button>
                    <button className={showVideoGallery ? 'flex-auto pb-2 border-b-2 border-blue-500 dark:border-blue-50 text-blue-500 dark:text-blue-50' : 'flex-auto pb-2 text-gray-600 dark:text-white-600 hover:text-gray-800 dark:hover:text-gray-80'} onClick={ () => chooseGalleryType("VIDEOS") }>
                      Videos
                    </button>
                  </div>
                </div>
              </div>
            )}

            { (!isSearching && dataFetchFailed) ? (
              <div className='text-center py-16'>
                <div className='text-6xl mb-4'>⚠️</div>
                <h2 className='text-xl font-medium text-gray-700 mb-1'>
                  Something went wrong, please try again in some time!
                </h2>
              </div>
            ) : (
              ""
            )}

            {/* Search state */}
            {isSearching ? (
              <div className='text-center py-12'>
                <button disabled type="button" class="text-white bg-blue-700 dark:text-gray-900 dark:bg-blue-70 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 inline-flex items-center">
                  <svg aria-hidden="true" role="status" class="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                  </svg>
                  Searching for {searchQuery}...
                </button>
              </div>
            ) : images.length > 0 ? (
              <>
                {/* Results info */}
                <p className='text-sm text-gray-500 dark:text-white-500 mb-4'>
                  Showing {showImageGallery ? images.length : videos.length} results for "{finalQuery}"
                </p>
                { useGrounding ? (
                  <p className='text-sm text-gray-500 dark:text-white-500 mb-4 line-clamp-3'>
                    Grounded Answer: {llmAnswer !== '' ? llmAnswer : "unable to fetch grounded results"}"
                  </p>
                ) : ""}

                

                {/* Image/Video grid */}
                {!galleryView ? (
                  <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                    { showImageGallery && images.map((image, index) => (
                      <div
                        key={image.id}
                        className='group cursor-pointer'
                        onClick={() => openImageGallery(index)}
                      >
                        <div className='bg-white rounded shadow overflow-hidden hover:shadow-md transition-shadow duration-200'>
                          <div className='h-40 overflow-hidden'>
                            <img
                              src={image.url}
                              alt={image.alt}
                              className='w-full h-full object-contain group-hover:opacity-90 transition-opacity duration-200'
                            />
                          </div>
                          <div className='p-3'>
                            <p className='text-sm font-medium text-blue-600 dark:text-blue-60 hover:underline line-clamp-2'>
                              {image.source_metadata.title}
                            </p>
                          </div>
                          <div className="p-3">
                          {image.attribution_text ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {image.attribution_text}
                            </span>
                          ) : (
                            ""
                          )}

                          {image.height && image.width ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {image.height + "x" + image.height}
                            </span>
                          ) : (
                            ""
                          )}

                          {image.source_metadata.creation_date ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {image.source_metadata.creation_date}
                            </span>
                          ) : (
                            ""
                          )}

                          {(image.matched_entities && image.matched_entities.length !== 0) ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {image.matched_entities[0].namespace} {image.matched_entities[0].id}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                        </div>
                      </div>
                    ))}
                    { showVideoGallery && videos.map((video, index) => (
                      <div
                        key={video.id}
                        className='group cursor-pointer'
                        onClick={() => openVideoInNewTab(video.source_url)}
                      >
                        <div className='bg-white rounded shadow overflow-hidden hover:shadow-md transition-shadow duration-200'>
                          <div className='h-40 overflow-hidden'>
                            <YoutubeEmbed embedId={video.id} />
                          </div>
                          <div className='p-3'>
                            <p className='text-sm font-medium text-blue-600 dark:text-blue-60 hover:underline line-clamp-2'>
                              {video.source_metadata.title}
                            </p>
                          </div>
                          <div className="p-3">
                          {video.source_type ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {video.source_type}
                            </span>
                          ) : (
                            ""
                          )}

                          {video.source_metadata.published_date ? (
                            <span className="mr-1 text-sm rounded border border-neutral-300 bg-white px-1 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-50">
                              {video.source_metadata.published_date}
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className='text-center py-16'>
                <div className='text-6xl mb-4'>🔍</div>
                <h2 className='text-xl font-medium text-gray-700 mb-1'>
                  Search for images and videos
                </h2>
              </div>
            )}
          </div>
        </main>

        {/* Full-size image modal */}
        {galleryView && showImageGallery && selectedItem && (
          <div
            className='fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center'
            onClick={closeGallery}
          >
            <div
              className='max-w-4xl w-full max-h-screen p-4'
              onClick={e => e.stopPropagation()}
            >
              <div className='relative'>
                <button
                  onClick={closeGallery}
                  className='absolute -top-12 right-0 text-white hover:text-gray-300'
                >
                  <svg
                    className='w-8 h-8'
                    fill='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                  </svg>
                </button>

                <div className='flex justify-between items-center mb-4'>
                  <button
                    onClick={() => navigateGallery(-1)}
                    className='bg-white bg-opacity-25 hover:bg-opacity-50 rounded-full p-2 text-black'
                  >
                    <svg
                      className='w-6 h-6'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
                    </svg>
                  </button>

                  <div className='bg-white text-black bg-opacity-90 px-4 py-2 rounded'>
                    <h3 className='font-medium'>{selectedItem.source_metadata.title}</h3>
                  </div>

                  <button
                    onClick={() => navigateGallery(1)}
                    className='bg-white bg-opacity-25 hover:bg-opacity-50 rounded-full p-2 text-black'
                  >
                    <svg
                      className='w-6 h-6'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' />
                    </svg>
                  </button>
                </div>

                <div className='bg-white p-4 rounded shadow-lg flex justify-center'>
                  <img
                    src={selectedItem.url}
                    alt={selectedItem.alt}
                    className='max-h-96 object-contain'
                  />
                </div>

                <div className='mt-4 bg-white text-gray-600 p-3 rounded shadow'>
                  <div className='flex justify-between items-center'>
                    <div>
                      <h4 className='font-medium'>{selectedItem.source_metadata.caption}</h4>
                    </div>
                    <div className='flex space-x-3'>
                      <button className='p-2 text-blue-500 hover:text-blue-500'>
                        <svg
                          className='w-5 h-5'
                          fill='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <a href={selectedItem.url} download="image">
                            <path d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z' />
                          </a>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    {selectedItem.attribution_text ? (
                      <span className="mr-1 text-sm rounded border border-neutral-300 px-1 bg-neutral-900 text-neutral-50">
                        {selectedItem.attribution_text}
                      </span>
                    ) : (
                      ""
                    )}

                    {selectedItem.height && selectedItem.width ? (
                      <span className="mr-1 text-sm rounded border border-neutral-300 px-1 bg-neutral-900 text-neutral-50">
                        {selectedItem.height + "x" + selectedItem.height}
                      </span>
                    ) : (
                      ""
                    )}

                    {selectedItem.source_metadata.creation_date ? (
                      <span className="mr-1 text-sm rounded border border-neutral-300 px-1 bg-neutral-900 text-neutral-50">
                        {selectedItem.source_metadata.creation_date}
                      </span>
                    ) : (
                      ""
                    )}

                    {(selectedItem.matched_entities && selectedItem.matched_entities.length !== 0) ? (
                      <span className="mr-1 text-sm rounded border border-neutral-300 px-1 bg-neutral-900 text-neutral-50">
                        {selectedItem.matched_entities[0].namespace} {selectedItem.matched_entities[0].id}
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    { user ? <MultimodalSearch /> : <p>authenticating...</p> }
    </>
  );
}

